/**
 * 8.3 — Taux de complétion des apprentissages.
 *
 * ⚠️ C'est une mesure RELATIVE, et c'est le point à ne pas rater. Le tracker
 * n'enregistre pas de fin de morceau : on ne sait pas si quelqu'un est allé au
 * bout. On compare donc le nombre de notes jouées à la MÉDIANE observée pour ce
 * morceau.
 *
 * Conséquence arithmétique : par construction, la moitié des tentatives d'un
 * morceau sont sous sa médiane. « 50 % de complétion » ne veut pas dire « la
 * moitié finissent », mais « ils ont joué au moins la moitié de ce qu'on joue
 * habituellement sur ce morceau ». C'est utile pour COMPARER des morceaux et
 * des catégories entre eux ; ça ne donne pas un taux d'achèvement absolu.
 */

import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import {
  ABANDON_THRESHOLD, COMPLETION_THRESHOLD, MIN_ATTEMPTS_FOR_MEDIAN,
} from '@shared/types/stats';

import { formatCompact, formatInteger, formatPercent } from '../../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS, SEQUENTIAL_RAMP } from '../../lib/theme';
import type {
  AbandonedTrack, CompletionAnalysis, CompletionByGroup, DataColumn, LabelledBucket,
} from '../../types';
import { Card, ChartCard } from '../Card';
import { ChartLegend, ChartTooltip, type LegendEntry } from '../ChartTooltip';
import { ReadingBox } from '../ReadingBox';
import { StatTile } from '../StatTile';

const legend: readonly LegendEntry[] = [
  { label: 'Complété', color: COLORS.series1, shape: 'area' },
  { label: 'Abandonné tôt', color: COLORS.series2, shape: 'area' },
];

const groupColumns: ReadonlyArray<DataColumn<CompletionByGroup>> = [
  { key: 'label', header: 'Groupe', render: (row) => row.label },
  { key: 'attempts', header: 'Tentatives', numeric: true, render: (row) => formatInteger(row.attempts) },
  { key: 'done', header: 'Complété', numeric: true, render: (row) => formatPercent(row.completedShare) },
  { key: 'quit', header: 'Abandonné', numeric: true, render: (row) => formatPercent(row.abandonedShare) },
];

const ratioColumns: ReadonlyArray<DataColumn<LabelledBucket>> = [
  { key: 'label', header: 'Notes jouées / médiane du morceau', render: (row) => row.label },
  { key: 'users', header: 'Tentatives', numeric: true, render: (row) => formatInteger(row.users) },
];

function GroupChart({
  data, title, subtitle,
}: { data: readonly CompletionByGroup[]; title: string; subtitle: string }) {
  const height = Math.max(240, data.length * 46 + 40);

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      emptyMessage="Pas assez de tentatives pour ce découpage."
      rows={data}
      columns={groupColumns}
      chartHeight={height}
    >
      <ChartLegend entries={legend} />
      <ResponsiveContainer width="100%" height="88%">
        <BarChart data={data as CompletionByGroup[]} layout="vertical" margin={{ top: 4, right: 24, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={(value: number) => `${value} %`}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={160}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip valueFormatter={formatPercent} />}
          />
          {/* Groupees et non empilees : les deux parts ne font pas 100 %, il
              reste la zone intermediaire entre 20 et 50 % de la mediane. */}
          <Bar isAnimationActive={false} dataKey="completedShare" name="Complété" fill={COLORS.series1} radius={[0, 4, 4, 0]} maxBarSize={BAR_MAX_SIZE} />
          <Bar isAnimationActive={false} dataKey="abandonedShare" name="Abandonné tôt" fill={COLORS.series2} radius={[0, 4, 4, 0]} maxBarSize={BAR_MAX_SIZE} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function CompletionPanel({ data }: { data: CompletionAnalysis }) {
  const bestCategory = [...data.byCategory].sort((a, b) => b.completedShare - a.completedShare)[0];
  const worstCategory = [...data.byCategory].sort((a, b) => b.abandonedShare - a.abandonedShare)[0];

  const easy = data.byDifficulty[0];
  const hard = data.byDifficulty[data.byDifficulty.length - 1];
  const difficultyTrend =
    easy && hard && hard !== easy
      ? hard.abandonedShare > easy.abandonedShare + 2
        ? `L'abandon croît avec la difficulté : ${formatPercent(easy.abandonedShare)} au ${easy.label.toLowerCase()} contre ${formatPercent(hard.abandonedShare)} au ${hard.label.toLowerCase()}.`
        : `L'abandon ne suit pas la difficulté annoncée : ${formatPercent(easy.abandonedShare)} au ${easy.label.toLowerCase()} contre ${formatPercent(hard.abandonedShare)} au ${hard.label.toLowerCase()}.`
      : '';

  return (
    <>
      <ReadingBox
        method={
          <>
            Une tentative est dite <strong>complétée</strong> si les notes jouées atteignent{' '}
            {formatPercent(COMPLETION_THRESHOLD * 100)} de la médiane du morceau, et{' '}
            <strong>abandonnée tôt</strong> sous {formatPercent(ABANDON_THRESHOLD * 100)}. Seuls
            les morceaux comptant au moins {MIN_ATTEMPTS_FOR_MEDIAN} tentatives ont une médiane
            exploitable. Mesure <em>relative</em> : le tracker n'enregistre pas de fin de morceau,
            donc par construction la moitié des tentatives d'un morceau sont sous sa médiane. Ces
            taux servent à comparer morceaux et catégories entre eux, pas à lire un taux
            d'achèvement absolu.
          </>
        }
      >
        {data.analysedAttempts === 0
          ? 'Aucun morceau ne réunit assez de tentatives sur cette période pour calculer une médiane fiable.'
          : `${formatPercent(data.abandonedShare)} des tentatives s'arrêtent avant un cinquième de ce qu'on joue habituellement sur le morceau.`}
        {bestCategory && worstCategory && bestCategory.label !== worstCategory.label
          ? ` La catégorie la mieux menée est « ${bestCategory.label} » (${formatPercent(bestCategory.completedShare)} complétées), la plus délaissée « ${worstCategory.label} » (${formatPercent(worstCategory.abandonedShare)} d'abandons).`
          : ''}
        {difficultyTrend ? ` ${difficultyTrend}` : ''}
      </ReadingBox>

      <div className="kpi-row">
        <StatTile
          label="Tentatives analysées"
          value={formatInteger(data.analysedAttempts)}
          hint={`morceaux à ${MIN_ATTEMPTS_FOR_MEDIAN} tentatives et +`}
        />
        <StatTile
          label="Complétées"
          value={formatPercent(data.completedShare)}
          hint="au moins 50 % de la médiane du morceau"
        />
        <StatTile
          label="Abandonnées tôt"
          value={formatPercent(data.abandonedShare)}
          hint="moins de 20 % de la médiane"
        />
      </div>

      <div className="chart-grid">
        <ChartCard
          title="Où s'arrêtent les apprentissages"
          subtitle="Notes jouées rapportées à la médiane du morceau"
          rows={data.ratioHistogram}
          columns={ratioColumns}
          chartHeight={280}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.ratioHistogram as LabelledBucket[]} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: COLORS.textSecondary, fontSize: 11 }}
                axisLine={{ stroke: COLORS.grid }}
                tickLine={false}
                interval={0}
              />
              <YAxis
                tickFormatter={formatCompact}
                tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
                axisLine={false}
                tickLine={false}
                width={48}
              />
              <Tooltip
                cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                content={<ChartTooltip valueFormatter={formatInteger} />}
              />
              <Bar isAnimationActive={false} dataKey="users" name="Tentatives" radius={[4, 4, 0, 0]} maxBarSize={BAR_MAX_SIZE * 2}>
                {data.ratioHistogram.map((bucket, index) => (
                  <Cell key={bucket.label} fill={SEQUENTIAL_RAMP[Math.min(index, SEQUENTIAL_RAMP.length - 1)]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <GroupChart
          data={data.byDifficulty}
          title="Complétion par niveau"
          subtitle="Difficulté annoncée par le catalogue"
        />

        <div className="chart-grid__wide">
          <GroupChart
            data={data.byCategory}
            title="Complétion par catégorie"
            subtitle="Écoutes exclues — uniquement les apprentissages"
          />
        </div>

        <div className="chart-grid__wide">
          <Card
            title="Morceaux les plus abandonnés"
            subtitle="Au moins 30 tentatives — sous ce seuil, un morceau joué trois fois coifferait le classement"
          >
            {data.mostAbandoned.length === 0 ? (
              <p className="empty" style={{ height: 100 }}>
                Aucun morceau ne réunit assez de tentatives.
              </p>
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }} className="num">#</th>
                      <th>Morceau</th>
                      <th className="num">Tentatives</th>
                      <th className="num">Abandon</th>
                      <th className="num">Complétion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.mostAbandoned.map((track: AbandonedTrack, index) => (
                      <tr key={track.trackName}>
                        <td className="num" style={{ color: COLORS.textMuted }}>{index + 1}</td>
                        <td className="wrap" title={track.trackName}>{track.label}</td>
                        <td className="num">{formatInteger(track.attempts)}</td>
                        <td className="num">{formatPercent(track.abandonedShare)}</td>
                        <td className="num">{formatPercent(track.completedShare)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
